import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Alert, Button, CircularProgress } from "@mui/material";
import { NotificationActions } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { IChannel } from "@Interfaces/Entities/Channels";
import { IPresentationAdmin } from "@Interfaces/Entities/Presentation";
import { PresentationService } from "@Services/API/Presentation";
import { copyToClipboard } from "@Utils/copyToClipboard";
import {
    TendbeQRCode,
    TENDBE_QR_LOGO,
} from "@Components/TendbeQRCode/TendbeQRCode";
import {
    LaptopIcon,
    MonitorIcon,
    PhoneIcon,
} from "@Components/Stage/LineIcons";
import {
    buildRemoteUrl,
    buildScreenUrl,
    formatLinkForDisplay,
    getIneligibleReasonKey,
} from "@Utils/presentation";
import {
    Card,
    Cards,
    CardTitle,
    CloseButton,
    ConnectedRow,
    ComputerCard,
    ComputerLabel,
    CopyButton,
    DialogBody,
    DisconnectButton,
    Divider,
    FieldLabel,
    Footer,
    Header,
    HeaderText,
    Hint,
    IconTile,
    Keys,
    LinkGroup,
    LinkInput,
    LinkRow,
    PhoneHeader,
    PhoneHint,
    PhoneTitle,
    PhoneTitleRow,
    PresentButton,
    QRBox,
    QRRow,
    Section,
    SectionTitle,
    Subtitle,
    Title,
} from "./PresentModeDialog.styles";

interface IPresentModeDialogProps {
    channel: IChannel;
}

type LoadState = "loading" | "ready" | "error";

const REFRESH_MS = 3000;

const PresentModeDialogContent: React.FC<IPresentModeDialogProps> = ({
    channel,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const [loadState, setLoadState] = useState<LoadState>("loading");
    const [presentation, setPresentation] = useState<IPresentationAdmin | null>(
        null
    );
    const [isStarting, setIsStarting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    const notify = useCallback(
        (snackbarType: "success" | "error", key: string) =>
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType,
                    snackbarMessage: t(key),
                })
            ),
        [dispatch, t]
    );

    // Bumped by every fetch and by Disconnect, so a slower, older answer is dropped.
    const latestRequest = useRef(0);

    const load = useCallback(async () => {
        const request = ++latestRequest.current;
        setLoadState("loading");
        try {
            const next = await PresentationService.getAdmin(channel._id);
            if (request !== latestRequest.current) return;
            setPresentation(next);
            setLoadState("ready");
        } catch {
            if (request === latestRequest.current) setLoadState("error");
        }
    }, [channel._id]);

    useEffect(() => {
        load();
    }, [load]);

    // The phone claims the remote on another device: check in while the dialog is open so
    // "Phone connected" and Disconnect appear without reopening it.
    useEffect(() => {
        if (loadState !== "ready") return;
        let active = true;
        const refresh = async () => {
            if (document.visibilityState !== "visible") return;
            const request = ++latestRequest.current;
            try {
                const next = await PresentationService.getAdmin(channel._id);
                if (active && request === latestRequest.current) {
                    setPresentation(next);
                }
            } catch {
                // Keep what's shown; the next check tries again.
            }
        };
        const interval = setInterval(refresh, REFRESH_MS);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [loadState, channel._id]);

    const close = () => dispatch(dialogActions.hideDialog());
    const screenUrl = presentation?.screen_code
        ? buildScreenUrl(presentation.screen_code)
        : "";
    const remoteUrl = presentation?.screen_code
        ? buildRemoteUrl(presentation.screen_code, presentation.remote_pin)
        : "";

    // As in the design, Copy turns into "Copied"; only a failure needs the snackbar.
    const handleCopy = async () => {
        if (await copyToClipboard(screenUrl)) {
            setCopied(true);
        } else {
            notify("error", "present.dialog.copy_failed");
        }
    };

    // Frees the remote and changes the PIN: the QR below updates, the old one stops working.
    const handleDisconnect = async () => {
        setIsDisconnecting(true);
        // A check sent before the reset finished would bring back the old PIN.
        latestRequest.current += 1;
        try {
            const next = await PresentationService.resetRemote(channel._id);
            latestRequest.current += 1;
            setPresentation(next);
        } catch {
            notify("error", "present.dialog.disconnect_failed");
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handlePresent = async () => {
        // Open the tab inside the click so the browser doesn't block it as a popup.
        const screenTab = window.open(screenUrl, "_blank");
        setIsStarting(true);
        try {
            await PresentationService.sendAction(channel._id, {
                type: "start",
            });
            close();
        } catch {
            screenTab?.close();
            notify("error", "present.dialog.start_failed");
        } finally {
            setIsStarting(false);
        }
    };

    const body = (() => {
        if (loadState === "loading") {
            return <CircularProgress sx={{ alignSelf: "center" }} />;
        }
        if (loadState === "error" || !presentation) {
            return (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            {t("present.dialog.retry")}
                        </Button>
                    }
                >
                    {t("present.dialog.load_failed")}
                </Alert>
            );
        }
        const { eligibility } = presentation;
        if (!eligibility.enabled && eligibility.reason) {
            return (
                <Alert severity="info">
                    {t(getIneligibleReasonKey(eligibility.reason))}
                </Alert>
            );
        }
        return (
            <Section>
                <SectionTitle>{t("present.dialog.how")}</SectionTitle>
                <Cards>
                    <Card aria-labelledby="present-phone-title">
                        <PhoneHeader>
                            <PhoneTitleRow>
                                <PhoneIcon size={28} />
                                <PhoneTitle id="present-phone-title">
                                    {t("present.dialog.with_phone")}
                                </PhoneTitle>
                            </PhoneTitleRow>
                            <PhoneHint>
                                {t("present.dialog.phone_hint")}
                            </PhoneHint>
                        </PhoneHeader>
                        <QRRow>
                            <QRBox>
                                <TendbeQRCode
                                    value={remoteUrl}
                                    qrColor={undefined}
                                    profileImgPath=""
                                    useChannelProfileAsQrLogo={false}
                                    logoImageOverride={TENDBE_QR_LOGO}
                                    qrSize={220}
                                    qrCodeHeight={52}
                                    qrCodeWidth={52}
                                    pixelRatio={2}
                                />
                            </QRBox>
                        </QRRow>
                        {presentation.remote_connected && (
                            <ConnectedRow role="status">
                                <span>
                                    {t("present.dialog.phone_connected")}
                                </span>
                                <DisconnectButton
                                    type="button"
                                    onClick={handleDisconnect}
                                    disabled={isDisconnecting}
                                >
                                    {t("present.dialog.disconnect")}
                                </DisconnectButton>
                            </ConnectedRow>
                        )}
                        <LinkGroup>
                            <FieldLabel
                                id="present-link-label"
                                htmlFor="present-link"
                            >
                                {t("present.dialog.link_label")}
                            </FieldLabel>
                            <LinkRow>
                                <LinkInput
                                    id="present-link"
                                    type="text"
                                    readOnly
                                    value={formatLinkForDisplay(screenUrl)}
                                    onFocus={(event) =>
                                        event.currentTarget.select()
                                    }
                                />
                                <CopyButton
                                    type="button"
                                    onClick={handleCopy}
                                    aria-live="polite"
                                >
                                    {copied
                                        ? t("present.dialog.copied")
                                        : t("present.dialog.copy")}
                                </CopyButton>
                            </LinkRow>
                            <Hint>{t("present.dialog.link_hint")}</Hint>
                        </LinkGroup>
                    </Card>
                    <Divider>{t("present.dialog.or")}</Divider>
                    <ComputerCard aria-labelledby="present-computer-title">
                        <ComputerLabel>
                            <LaptopIcon size={22} />
                            <CardTitle id="present-computer-title">
                                {t("present.dialog.from_computer")}
                            </CardTitle>
                        </ComputerLabel>
                        <Keys>
                            <span>{t("present.dialog.move_with")}</span>
                            <kbd>←</kbd>
                            <kbd>→</kbd>
                        </Keys>
                        <PresentButton
                            type="button"
                            onClick={handlePresent}
                            disabled={isStarting}
                        >
                            {t("present.dialog.present")}
                        </PresentButton>
                    </ComputerCard>
                </Cards>
            </Section>
        );
    })();

    return (
        <DialogBody role="document" aria-labelledby="present-mode-title">
            <Header>
                <IconTile aria-hidden="true">
                    <MonitorIcon size={32} />
                </IconTile>
                <HeaderText>
                    <Title id="present-mode-title">
                        {t("present.dialog.title")}
                    </Title>
                    <Subtitle>
                        {t("present.dialog.subtitle", { title: channel.title })}
                    </Subtitle>
                </HeaderText>
            </Header>
            {body}
            <Footer>
                <CloseButton type="button" onClick={close} autoFocus>
                    {t("present.dialog.close")}
                </CloseButton>
            </Footer>
        </DialogBody>
    );
};

const PresentModeDialogActions: React.FC = () => <></>;

export { DIALOG_WIDTH as PRESENT_MODE_DIALOG_WIDTH } from "./PresentModeDialog.styles";

export default {
    content: PresentModeDialogContent,
    actions: PresentModeDialogActions,
    title: "Present Mode",
    showActons: false,
    disableCancelButton: true,
    isGuestData: true,
};
